/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { Table } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/quote-management/components/QuotesListTable/QuotesListTable.css';

type Column = {
  key: string;
  label: string;
};

export interface QuotesListTableProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'loading' | 'children'> {
  rowData: QuoteRowData[];
  loading?: boolean;
  className?: string;
  emptyStateMessage?: VNode;
  showItemRange?: boolean;
  itemRangeMessage?: VNode;
  showPageSizePicker?: boolean;
  pageSizePickerMessage?: VNode;
  showPagination?: boolean;
  paginationMessage?: VNode;
}

export type QuoteRowData = {
  id: string;
  quoteName: VNode;
  created: VNode;
  createdBy: VNode;
  status: VNode;
  lastUpdated: VNode;
  quoteTemplate: VNode;
  quoteTotal: VNode;
  actions: VNode;
  [key: string]: VNode | string | number | undefined;
};

export const QuotesListTable: FunctionComponent<QuotesListTableProps> = ({
  rowData = [],
  loading = false,
  className,
  emptyStateMessage,
  showItemRange = true,
  itemRangeMessage,
  showPageSizePicker = true,
  pageSizePickerMessage,
  showPagination = true,
  paginationMessage,
  ...props
}) => {
  const dictionary = useText({
    quoteName: 'QuoteManagement.QuotesListTable.quoteName',
    created: 'QuoteManagement.QuotesListTable.created',
    createdBy: 'QuoteManagement.QuotesListTable.createdBy',
    status: 'QuoteManagement.QuotesListTable.status',
    lastUpdated: 'QuoteManagement.QuotesListTable.lastUpdated',
    quoteTemplate: 'QuoteManagement.QuotesListTable.quoteTemplate',
    quoteTotal: 'QuoteManagement.QuotesListTable.quoteTotal',
    actions: 'QuoteManagement.QuotesListTable.actions',
  });

  const columns: Column[] = [
    { key: 'quoteName', label: dictionary.quoteName },
    { key: 'created', label: dictionary.created },
    { key: 'createdBy', label: dictionary.createdBy },
    { key: 'status', label: dictionary.status },
    { key: 'lastUpdated', label: dictionary.lastUpdated },
    { key: 'quoteTemplate', label: dictionary.quoteTemplate },
    { key: 'quoteTotal', label: dictionary.quoteTotal },
    { key: 'actions', label: dictionary.actions },
  ];

  // Check if should show the table when loading or there is data
  const shouldShowTable = loading || rowData.length > 0;

  // Check if we should show empty state
  const shouldShowEmptyState =
    !loading && rowData.length === 0 && emptyStateMessage;

  // Show item range if requested and message is provided
  const shouldShowItemRange = showItemRange && itemRangeMessage;

  // Show page size picker if requested and message is provided
  const shouldShowPageSizePicker = showPageSizePicker && pageSizePickerMessage;

  // Show pagination if requested and message is provided
  const shouldShowPagination = showPagination && paginationMessage;

  // Show footer if any pagination element should be shown
  const shouldShowFooter =
    shouldShowItemRange || shouldShowPageSizePicker || shouldShowPagination;

  return (
    <div
      {...props}
      className={classes(['quote-management-quotes-list-table', className])}
    >
      {shouldShowTable && (
        <Table
          columns={columns}
          rowData={rowData}
          loading={loading}
          mobileLayout="none"
          className="quote-management-quotes-list-table__table"
        />
      )}
      {shouldShowEmptyState && (
        <div className="quotes-list-table__empty-state">
          {emptyStateMessage}
        </div>
      )}
      {shouldShowFooter && (
        <div className="quotes-list-table__footer">
          <div className="quotes-list-table__item-range">
            {shouldShowItemRange && itemRangeMessage}
          </div>
          <div className="quotes-list-table__pagination">
            {shouldShowPagination && paginationMessage}
          </div>
          <div className="quotes-list-table__page-size-picker">
            {shouldShowPageSizePicker && pageSizePickerMessage}
          </div>
        </div>
      )}
    </div>
  );
};
